import tw, { styled, css } from 'twin.macro';
import { Container as AnnoucementButtonContainer } from '@/components/AnnouncementButton/AnnouncementButton.styles';
import { StyledContainer as StarRatingContainer } from '@/components/StarRating/StarRating.styles';
import { StyledContainer as LinkGroupContainer } from '@/components/LinkGroup/LinkGroup.styles';
import { StyledContainer as InfoTagGroupContainer } from '@/components/InfoTagGroup/InfoTagGroup.styles';

const isAlignedLeft = ({
  imageOption,
  hasImage,
  hasSideText,
}: {
  hasImage: boolean;
  imageOption: Queries.SUPER_CTA_BLOCKFragment['superCTABlockElements']['image_option'];
  hasSideText: boolean;
}) => hasSideText || (hasImage && imageOption?.value !== 'background_fill');

export const StyledSection = styled.section<{
  backgroundColor?:
    | Queries.SUPER_CTA_BLOCKFragment['superCTABlockElements']['background_color']
    | null;
}>`
  ${tw`px-2 md:px-0`}
  ${tw`bg-secondary-s1`}

  ${({ backgroundColor }) => {
    switch (backgroundColor?.value) {
      case 'ghost_white':
        return tw`bg-ui-colours-ui1`;
      case 'secondary_blue':
        return tw`bg-secondary-s1`;
      default:
        return tw`bg-neutrals-n1`;
    }
  }}
`;

export const StyledImageContainer = styled.div<{
  image: Queries.SUPER_CTA_BLOCKFragment['superCTABlockElements']['image'] | undefined;
}>`
  ${tw`bg-cover bg-center`}
  ${tw`-z-1`}
  ${({ image }) => {
    return image?.value?.url ? `background-image: url(${image.value.url});` : '';
  }}
`;

export const StyledContent = styled.div`
  ${tw`flex flex-col justify-center items-center`}
  ${tw`p-3 md:p-4`}
`;

export const StyledRichText = styled.div`
  ${tw`w-full`}
  ${tw`grid grid-cols-12 gap-[20px]`}

  & > * {
    ${tw`col-span-full`}
  }

  & > p {
    ${tw`font-poppins`}
    ${tw`text-body-xl`}
    ${tw`text-primary-p1`}
  }

  & > h2 {
    ${tw`font-poppins font-semi-bold`}
    ${tw`text-mobile-display-xl lg:text-display-xl text-secondary-s1`}
  }
`;

export const StyledContainer = styled.div<{
  hasImage: boolean;
  imageOption: Queries.SUPER_CTA_BLOCKFragment['superCTABlockElements']['image_option'];
  hasSideText: boolean;
}>`
  ${tw`container`}
  ${tw`container-stack-context`}
  ${tw`bg-neutrals-n1`}
  ${tw`border-4 border-primary-p1`}
  ${tw`rounded-xl`}
  ${tw`overflow-hidden`}

  ${({ imageOption, hasSideText, hasImage }) => {
    switch (imageOption?.value) {
      case 'right_half_fill':
      case 'square_400x400':
        return !hasSideText && hasImage && tw`grid grid-cols-1 lg:grid-cols-2 gap-4`;
    }
  }}

  ${({ hasSideText }) => hasSideText && tw`grid grid-cols-1 lg:grid-cols-12`};

  ${StyledContent} {
    ${({ hasSideText }) => hasSideText && tw`col-span-8 lg:pr-7`}
  }

  ${StyledImageContainer} {
    ${({ imageOption }) => {
      switch (imageOption?.value) {
        case 'square_400x400':
          return tw`flex grow justify-center items-center p-4`;
        case 'background_fill':
          return tw`absolute top-0 left-0 right-0 bottom-0`;
        default:
          return tw`h-[400px] lg:h-auto`;
      }
    }}
  }

  ${AnnoucementButtonContainer}, ${StarRatingContainer}, ${LinkGroupContainer}, ${InfoTagGroupContainer} {
    ${({ hasSideText, hasImage, imageOption }) =>
      isAlignedLeft({ hasSideText, hasImage, imageOption })
        ? tw`justify-start`
        : tw`justify-center`}
  }

  ${StyledRichText} {
    ${({ hasSideText, hasImage, imageOption }) =>
      isAlignedLeft({ hasSideText, hasImage, imageOption }) ? tw`text-left` : tw`text-center`}

    ${({ hasSideText, hasImage, imageOption }) =>
      !isAlignedLeft({ hasSideText, hasImage, imageOption }) &&
      hasImage &&
      css`
        & > p,
        & > h2 {
          ${tw`text-neutrals-n1`}
        }
      `}
  }
`;

export const StyledImage = styled.img`
  ${tw`w-[400px] h-[400px]`}
`;

export const StyledSideRichText = styled.div`
  ${tw`font-inter font-regular`}
  ${tw`text-body-l`}

  h2 {
    ${tw`font-inter font-semi-bold`}
    ${tw`text-tag-m`}
    ${tw`mb-2`}
  }

  p:not(:last-child) {
    ${tw`mb-3`}
  }

  ul {
    li {
      ${tw`bg-[url("/svg/check-circle-pricing.svg")] bg-no-repeat bg-left-top`}
      ${tw`pl-[36px]`}
      ${tw`mb-[16px]`}
      ${tw`list-outside list-none`}
    }
  }
`;

export const StyledSideContainer = styled.div`
  ${tw`col-span-4`}
  ${tw`flex flex-col justify-center`}
  ${tw`p-3 lg:p-4`}
  ${tw`bg-ui-colours-ui1`}
`;
